import { ethers } from "ethers";
import BigNumber from "bignumber.js";
import { writeContract, waitForTransactionReceipt } from "@wagmi/core";
import { config as wagmiConfig } from "../../wagmi.config";
import ERC20 from "../../src/assets/abi/farm/ERC20.json";
import useLazyReadContractAsync from "./useLazyReadContractAsync";
import appConfig from "../config";

const noOp = () => {};

interface UseTokenOperationsProps {
  checkAllowanceToSpenderFromMax: (
    tokenSymbol: string,
    ownerAddress: string,
    spenderAddress: string,
    balance: BigNumber
  ) => Promise<boolean>;
  checkAllowanceToSpender: (
    tokenSymbol: string,
    ownerAddress: string,
    spenderAddress: string
  ) => Promise<boolean>;
  checkAllowanceAmountToSpender: (
    tokenSymbol: string,
    ownerAddress: string,
    spenderAddress: string
  ) => Promise<string | null>;
  approveTokenToSpender: (
    tokenSymbol: string,
    ownerAddress: string,
    spenderAddress: string,
    callbacks?: Callbacks
  ) => Promise<void>;
  approveTokenAmountToSpender: (
    tokenSymbol: string,
    ownerAddress: string,
    spenderAddress: string,
    amount: string | number,
    callbacks?: Callbacks
  ) => Promise<void>;
  getBalanceOf: (tokenId: string, account: string) => Promise<string | null>;
  getAllowance: (
    tokenId: string,
    owner: string,
    spender: string,
    contractAddress: string
  ) => Promise<string | null>;
}

interface Callbacks {
  onError?: (error: any) => void;
  onReceipt?: (receipt: any) => void;
  onTransactionHash?: (hash: string) => void;
}

export const useTokenHelpers = (): UseTokenOperationsProps => {
  const { lazyReadContract } = useLazyReadContractAsync();
  // @ts-ignore
  // const { data: balance, refetch } = useReadContract({
  //   // ...wagmiConfig,
  //   address: "0xd190454201DC1EeCb3b75b4B540c03bc2bE1C80A",
  //   functionName: "allowance",
  //   args: [
  //     "0x8991E6926A5ce660CC074d24372e172BFCEE3e1b",
  //     " 0x8427E482D12E4207a978D0ee3DE4e715c50C0c7F",
  //   ],
  // });

  // console.log("balance", balance);

  const checkAllowanceToSpender = async (
    tokenSymbol: string,
    ownerAddress: string,
    spenderAddress: string
  ): Promise<boolean> => {
    // console.log("hello world2");
    console.log("checkAllowanceToSpender", {
      tokenSymbol,
      ownerAddress,
      spenderAddress,
    });
    // if (!ethereum.contracts) return false;
    if (tokenSymbol === "BNB_TOKEN") return true;
    // if (!ethereum.contracts.tokens[tokenSymbol]) return false;
    try {
      const tokenAddress = appConfig.token[tokenSymbol]?.address;
      if (!tokenAddress) {
        console.warn(`Token address not found for: ${tokenSymbol}`);
        return false;
      }
      
      const allowance: any = await lazyReadContract(
        ERC20,
        tokenAddress,
        "allowance",
        [ownerAddress, spenderAddress]
      );

      console.log("checkAllowanceToSpender", {
        allowance,
      });

      // const allowance = await ethereum.contracts.tokens[tokenSymbol].methods
      //   .allowance(ownerAddress, spenderAddress)
      //   .call();
      return !BigNumber(allowance).isEqualTo(0);
    } catch (error) {
      return false;
    }
  };

  const checkAllowanceToSpenderFromMax = async (
    tokenSymbol: string,
    ownerAddress: string,
    spenderAddress: string,
    balance: BigNumber
  ): Promise<boolean> => {
    // if (!ethereum.contracts) return false;
    if (tokenSymbol === "BNB_TOKEN") return true;
    // if (!ethereum.contracts.tokens[tokenSymbol]) return false;
    try {
      const tokenAddress = appConfig.token[tokenSymbol]?.address;
      if (!tokenAddress) {
        console.warn(`Token address not found for: ${tokenSymbol}`);
        return false;
      }
      
      const allowance: any = await lazyReadContract(
        ERC20,
        tokenAddress,
        "allowance",
        [ownerAddress, spenderAddress]
      );

      console.log(
        BigNumber(allowance).div(1e18).toString(),
        " / balance =>",
        balance.toString()
      );

      return BigNumber(allowance).div(1e18).comparedTo(balance) >= 0;
    } catch (error) {
      return false;
    }
  };

  const checkAllowanceAmountToSpender = async (
    tokenSymbol: string,
    ownerAddress: string,
    spenderAddress: string
  ): Promise<string | null> => {
    if (tokenSymbol === "BNB_TOKEN") return null;
    
    try {
      const tokenAddress = appConfig.token[tokenSymbol]?.address;
      if (!tokenAddress) {
        console.warn(`Token address not found for: ${tokenSymbol}`);
        return null;
      }
      
      const allowance = await lazyReadContract(
        ERC20,
        tokenAddress,
        "allowance",
        [ownerAddress, spenderAddress]
      );
      
      return allowance?.toString() || "0";
    } catch (error) {
      console.error("Error getting allowance:", error);
      return null;
    }
  };

  const approveTokenToSpender = async (
    tokenAddress: string,
    ownerAddress: string,
    spenderAddress: string,
    {
      onError = () => {},
      onReceipt = noOp,
      onTransactionHash = noOp,
    }: Callbacks = {}
  ): Promise<void> => {
    try {
      console.log("approveTokenToSpender", tokenAddress, ownerAddress, spenderAddress);

      // ✅ Correct wagmi v2 syntax
      const hash = await writeContract(wagmiConfig, {
        abi: ERC20,
        address: tokenAddress as `0x${string}`,
        functionName: "approve",
        args: [spenderAddress as `0x${string}`, ethers.MaxUint256],
      });

      // ✅ hash is an object now (v2)
      if (onTransactionHash) onTransactionHash(hash);

      // ✅ waitForTransactionReceipt new signature
      const receipt = await waitForTransactionReceipt(wagmiConfig, { hash });

      if (onReceipt)
        onReceipt({
          transactionHash: receipt.transactionHash,
          status: receipt.status === "success",
        });

      console.log("approveTokenToSpender: success", receipt);
    } catch (error) {
      console.log("approveTokenToSpender: error", error);
      onError(error);
    }
  };


  const approveTokenAmountToSpender = async (
    tokenSymbol: string,
    ownerAddress: string,
    spenderAddress: string,
    amount: string | number,
    {
      onError = noOp,
      onReceipt = noOp,
      onTransactionHash = noOp,
    }: Callbacks = {}
  ): Promise<void> => {
    try {
      const tokenAddress = appConfig.token[tokenSymbol]?.address;
      if (!tokenAddress) {
        throw new Error(`Token address not found for: ${tokenSymbol}`);
      }
      
      const approveAmount =
        amount === -1
          ? ethers.MaxUint256.toString()
          : ethers.parseEther(amount.toString()).toString();
          
      const hash = await writeContract(wagmiConfig, {
        abi: ERC20,
        address: tokenAddress as `0x${string}`,
        functionName: "approve",
        args: [spenderAddress as `0x${string}`, BigInt(approveAmount)],
      });

      onTransactionHash(hash);
      const receipt = await waitForTransactionReceipt(wagmiConfig, { hash });
      onReceipt(receipt);
    } catch (error) {
      onError(error);
    }
  };

  const getBalanceOf = async (tokenId: string, account: string) => {
    try {
      const tokenAddress = appConfig.token[tokenId]?.address;
      if (!tokenAddress) {
        console.warn(`Token address not found for: ${tokenId}`);
        return null;
      }
      
      const balance = await lazyReadContract(
        ERC20,
        tokenAddress,
        "balanceOf",
        [account]
      );
      
      return balance?.toString() || "0";
    } catch (error) {
      console.log("Err: getBalanceOf", error);
      return null;
    }
  };

  const getAllowance = async (
    tokenId: string,
    owner: string,
    spender: string,
    contractAddress: string
  ): Promise<string | null> => {
    try {
      console.log("contractAddress", contractAddress);
      if (tokenId !== "BNB_TOKEN") {
        const allowance: any = await lazyReadContract(
          ERC20,
          contractAddress,
          "allowance",
          [owner, spender]
        );

        return allowance?.toString() || "0";
      }
      return null;
    } catch (error) {
      console.log("Err: getAllowance", error);
      return null;
    }
  };

  return {
    checkAllowanceToSpenderFromMax,
    checkAllowanceToSpender,
    checkAllowanceAmountToSpender,
    approveTokenToSpender,
    approveTokenAmountToSpender,
    getBalanceOf,
    getAllowance,
  };
};
